import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { appApi } from '@/api/app'
import { mentorApi } from '@/api/mentor'
import type { components } from '@/types/generated/api'
import { ROLE_KEY, TOKEN_KEY } from '@/utils/storage'

/** 业务角色（与后端 sys_role.role_key 对齐，见 initdb/04_business_baseline.sql） */
export type UserRole = 'ROLE_CANDIDATE' | 'ROLE_MENTOR'

type AppUserInfo = components['schemas']['AppUserInfo']
type LoginRequest = components['schemas']['LoginRequest']
type RegisterRequest = components['schemas']['RegisterRequest']

/** 已知业务角色 → 前端角色（无归属返回空串，调用方据此清理会话） */
function pickRole(roles: string[]): UserRole | '' {
  if (roles.includes('ROLE_CANDIDATE')) return 'ROLE_CANDIDATE'
  if (roles.includes('ROLE_MENTOR')) return 'ROLE_MENTOR'
  return ''
}

/** 校验持久化的角色值：旧版本/篡改残留的非法值视为无角色，否则角色互斥守卫会重定向回同路径形成死循环 */
function normalizeRole(raw: string | null): UserRole | '' {
  return raw === 'ROLE_CANDIDATE' || raw === 'ROLE_MENTOR' ? raw : ''
}

/**
 * 认证会话。
 * - 求职者端（WP3）：真实接口（register/login/me）；token + role 持久化，
 *   user 不持久化——刷新后经 restore()（GET /me）拉取，保证角色/昵称新鲜。
 * - 导师端：/api/mentor/auth/login + me（后端已实现）；token + role 持久化。
 */
export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) ?? '')
  const role = ref<UserRole | ''>(normalizeRole(localStorage.getItem(ROLE_KEY)))
  const user = ref<AppUserInfo | null>(null)

  const isAuthenticated = computed(() => token.value !== '')
  /** 所属端首页（登录后跳转 / 角色互斥回落目标） */
  const homePath = computed(() =>
    role.value === 'ROLE_MENTOR' ? '/mentor/workbench' : '/app/workspace',
  )

  function applySession(nextToken: string, info: AppUserInfo, forcedRole?: UserRole) {
    token.value = nextToken
    user.value = info
    role.value = forcedRole ?? pickRole(info.roles ?? [])
    localStorage.setItem(TOKEN_KEY, nextToken)
    if (role.value) {
      localStorage.setItem(ROLE_KEY, role.value)
    } else {
      localStorage.removeItem(ROLE_KEY)
    }
  }

  /** 求职者登录（失败抛 BizError，由页面展示 message） */
  async function login(payload: LoginRequest) {
    const result = await appApi.auth.login(payload)
    applySession(result.token, result.user)
  }

  /** 求职者注册（成功即登录） */
  async function register(payload: RegisterRequest) {
    const result = await appApi.auth.register(payload)
    applySession(result.token, result.user)
  }

  /** 导师登录（/api/mentor/auth/login）；双角色用户选导师门户时钉死 ROLE_MENTOR（pickRole 候选优先会弹回 C 端） */
  async function mentorLogin(payload: LoginRequest) {
    const result = await mentorApi.auth.login(payload)
    applySession(result.token, result.user as unknown as AppUserInfo, 'ROLE_MENTOR')
  }

  /**
   * 会话恢复：有 token 但 user 未加载（页面刷新）时拉取 /me。
   * 按角色分发：ROLE_MENTOR → /api/mentor/auth/me，其余 → /api/app/auth/me。
   * dev 伪 token（devLogin）无后端会话，跳过；恢复失败或角色无归属时清理会话。
   * 注：若 localStorage 已有角色（E2E 覆盖/手动切换），保留该角色不覆盖。
   */
  async function restore() {
    if (!isAuthenticated.value || user.value || token.value.startsWith('dev.')) return
    try {
      let info: AppUserInfo | undefined
      // E2E 场景：localStorage 角色为 MENTOR 但 JWT 实际为 CANDIDATE，mentor endpoint 会 401
      if (role.value === 'ROLE_MENTOR') {
        try {
          info = await mentorApi.auth.me()
        } catch {
          // mentor endpoint 失败 → 回退到 candidate endpoint（token 可能是 candidate 的）
          info = await appApi.auth.me()
        }
      } else {
        info = await appApi.auth.me()
      }
      const nextRole = pickRole(info.roles ?? [])
      if (!nextRole) {
        logout()
        return
      }
      user.value = info as unknown as AppUserInfo
      // 保留 localStorage 中已设置的合法角色（E2E 测试覆盖 / 用户手动切换），非法值不覆盖
      const storedRole = normalizeRole(localStorage.getItem(ROLE_KEY))
      if (storedRole && storedRole !== nextRole) {
        // localStorage 角色与 API 返回不一致时，以 localStorage 为准（E2E 覆盖场景）
        role.value = storedRole
      } else {
        role.value = nextRole
        localStorage.setItem(ROLE_KEY, nextRole)
      }
    } catch {
      logout()
    }
  }

  /** 导师端开发期通道（后端 /api/mentor 未实现；仅演示双端布局与角色互斥） */
  function devLogin(nextRole: UserRole) {
    token.value = `dev.${Date.now()}`
    role.value = nextRole
    user.value = null
    localStorage.setItem(TOKEN_KEY, token.value)
    localStorage.setItem(ROLE_KEY, role.value)
  }

  function logout() {
    token.value = ''
    role.value = ''
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ROLE_KEY)
  }

  return {
    token,
    role,
    user,
    isAuthenticated,
    homePath,
    login,
    register,
    mentorLogin,
    restore,
    devLogin,
    logout,
  }
})
